import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import {
  validateFieldValue,
  EXTENSION_FIELD_TYPE_IDS,
} from "../../../utils/extensionFieldUtils";
import { ExtensionFieldCellEditor, CellEditorParams } from "./types";
import {
  CELL_EDITOR_STYLES,
  handleCellEditorKeyboard,
  handleNumericPaste,
  isAllowedKey,
  updateValidationState,
} from "./utils/cellEditorStyles";

/**
 * Number Cell Editor for integer and float extension fields
 * Provides number-specific validation and formatting for AG Grid inline editing
 */
export class NumberCellEditor implements ExtensionFieldCellEditor {
  private eGui: HTMLElement;
  private params!: CellEditorParams;
  private inputElement: HTMLInputElement;
  private currentValue: string;
  private initialValue: string;
  private isInteger: boolean;

  constructor() {
    this.eGui = document.createElement("div");
    Object.assign(this.eGui.style, CELL_EDITOR_STYLES.container);
    this.currentValue = "";
    this.initialValue = "";
    this.isInteger = false;

    this.inputElement = document.createElement("input");
    this.setupInputElement();
  }

  private setupInputElement(): void {
    this.inputElement.name = "cellValue";
    Object.assign(this.inputElement.style, CELL_EDITOR_STYLES.input);

    // Add event listeners directly (AG Grid handles optimization)
    this.inputElement.addEventListener("input", this.handleInputChange);
    this.inputElement.addEventListener("keydown", this.handleKeyDown);
    this.inputElement.addEventListener("blur", this.handleBlur);
    this.inputElement.addEventListener("paste", this.handlePaste);

    this.eGui.appendChild(this.inputElement);
  }

  private handleInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.currentValue = newValue;

    this.updateValidationState(newValue);
  };

  private updateValidationState(value: string): void {
    const validationError = validateFieldValue(value, this.params.fieldType);
    updateValidationState(this.inputElement, validationError);
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Handle number-specific validation first
    const isNumberKey = /^[0-9]$/.test(event.key);
    const isDecimalPoint =
      event.key === "." && !this.isInteger && !this.inputElement.value.includes(".");
    const isMinusSign = event.key === "-" && this.inputElement.value.length === 0;

    if (!isAllowedKey(event) && !isNumberKey && !isDecimalPoint && !isMinusSign) {
      event.preventDefault();
      return;
    }

    if (event.key === "Enter") {
      const validationError = validateFieldValue(
        this.inputElement.value,
        this.params.fieldType,
      );
      if (validationError) {
        this.updateValidationState(this.inputElement.value);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    // Use shared keyboard handler for Enter/Escape behavior
    handleCellEditorKeyboard(event, {
      inputElement: this.inputElement,
      initialValue: this.initialValue,
      currentValueSetter: (value) => {
        this.currentValue = value;
      },
      api: this.params.api,
    });
  };

  private handleBlur = (): void => {
    const validationError = validateFieldValue(this.inputElement.value, this.params.fieldType);
    if (validationError) {
      this.inputElement.value = this.initialValue;
      this.currentValue = this.initialValue;
      this.updateValidationState(this.initialValue);
      this.params.api.stopEditing(true);
      return;
    }

    this.params.api.stopEditing();
  };

  private handlePaste = (event: ClipboardEvent): void => {
    handleNumericPaste(event, this.inputElement, /[^0-9.-]/g, (value) => {
      this.currentValue = value;
    });
  };

  init(params: CellEditorParams): void {
    this.params = params;
    this.isInteger = params.fieldType === EXTENSION_FIELD_TYPE_IDS.INTEGER;

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value as ExtensionFieldValueObjectType | null;

    if (this.isInteger) {
      this.initialValue = extensionFieldValue?.intValue?.toString() || "";
    } else {
      this.initialValue = extensionFieldValue?.floatValue?.toString() || "";
    }
    this.currentValue = this.initialValue;

    // Set placeholder and input value
    const placeholder = this.isInteger ? "Enter integer..." : "Enter number...";
    this.inputElement.placeholder = placeholder;
    this.inputElement.value = this.currentValue;
  }

  afterGuiAttached(): void {
    if (this.inputElement) {
      this.inputElement.focus();
      this.inputElement.select();
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    if (this.inputElement) {
      this.currentValue = this.inputElement.value;
    }
    return this.currentValue;
  }

  destroy(): void {
    // AG Grid handles cleanup automatically, minimal cleanup needed
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  focusIn(): void {
    if (this.inputElement) {
      this.inputElement.focus();
    }
  }

  focusOut(): void {
    // No specific action needed on focus out
  }
}
